import { BasicInfoStepValues } from 'modules/onboarding/steps/BasicInfo/BasicInfoStep';
import { EducationStepValues } from 'modules/onboarding/steps/Education/EducationStep';
import { ExperienceStepValues } from 'modules/onboarding/steps/Experience/ExperienceStep';
import { LanguagesStepValues } from 'modules/onboarding/steps/Languages/LanguagesStep';
import { SkillStepValues } from 'modules/onboarding/steps/Skill/SkillStep';
import { SkillLevelStepValues } from 'modules/onboarding/steps/SkillLevel/SkillLevelStep';
import { FinishedOnboardingValues } from 'modules/onboarding/utils/buildDefaultValues';
import { extractSkillsFromPrevStates } from 'modules/onboarding/utils/extractSkillsFromPrevStates';
import { filterEmptyCertificates } from 'modules/onboarding/utils/filterEmptyCertificates';
import { filterEmptyEducation } from 'modules/onboarding/utils/filterEmptyEducation';
import { ProfileTabEnum } from 'modules/profile/types/profile-tab.enum';
import { ValuesByName } from 'modules/profile/utils/mapValuesForApiUpdate';
import { ExpertDataQuery } from 'shared/hooks/api/expertData/expertData.generated';
import { ExpertProfile } from 'types/ExpertProfile/ExpertProfile';
import { mapDtoToExpertProfile } from 'types/ExpertProfile/mappers';
import { Skill } from 'types/Skill';
import { WorkHistory } from 'types/WorkHistory';
import { create } from 'zustand';

interface ExpertProfileStore {
  expertProfile: ExpertProfile | null;
  loadExpertProfile: (
    dto: ExpertDataQuery['expertHubData'],
    exportId?: string,
    email?: string,
  ) => void;
  updateExpertProfileFromOnboarding: (onboardingValues: FinishedOnboardingValues) => void;
  updateExpertProfileFromProfile: <T extends ProfileTabEnum>(
    tab: T,
    newValues: Partial<ValuesByName[T]>,
  ) => void;
  clearExpertProfile: () => void;
}

export const useExpertProfileStore = create<ExpertProfileStore>((set) => ({
  expertProfile: null,
  loadExpertProfile: (dto: ExpertDataQuery['expertHubData'], exportId?: string, email?: string) =>
    set((prev) => ({
      expertProfile: {
        ...mapDtoToExpertProfile(dto),
        id: exportId ?? prev.expertProfile?.id ?? '',
        email: email ?? prev.expertProfile?.email ?? '',
      },
    })),
  updateExpertProfileFromOnboarding: (onboardingValues: FinishedOnboardingValues) =>
    set((prev) => ({
      ...(prev.expertProfile && {
        expertProfile: {
          ...prev.expertProfile,
          ...(onboardingValues.firstName && { firstName: onboardingValues.firstName }),
          ...(onboardingValues.lastName && { lastName: onboardingValues.lastName }),
          ...(onboardingValues.location && {
            location: onboardingValues.location,
          }),
          ...(onboardingValues.rateDesired && { rateDesired: onboardingValues.rateDesired }),
          ...(onboardingValues.workType && { workType: onboardingValues.workType }),
          ...(onboardingValues.availability && { availability: onboardingValues.availability }),
          ...(onboardingValues.jobPositionId && { jobPositionId: onboardingValues.jobPositionId }),
          ...(onboardingValues.firstLanguageId && {
            firstLanguageId: onboardingValues.firstLanguageId,
          }),
          ...(typeof onboardingValues.isOnlyFirstLanguage === 'boolean' && {
            isOnlyFirstLanguage: onboardingValues.isOnlyFirstLanguage,
          }),
          ...(onboardingValues.otherLanguages && {
            otherLanguages: onboardingValues.otherLanguages,
          }),
          ...(onboardingValues.mainWorkHistory && {
            mainWorkHistory: onboardingValues.mainWorkHistory,
          }),
          ...(onboardingValues.otherWorkHistory && {
            otherWorkHistory: onboardingValues.otherWorkHistory,
          }),
          ...(typeof onboardingValues.isMainRoleExperience === 'boolean' && {
            isMainRoleExperience: onboardingValues.isMainRoleExperience,
          }),
          ...(typeof onboardingValues.isOtherRolesExperience === 'boolean' && {
            isOtherRolesExperience: onboardingValues.isOtherRolesExperience,
          }),
          ...(onboardingValues.skills && { skills: onboardingValues.skills }),
          ...(onboardingValues.manuallyAddedSkills && {
            manuallyAddedSkills: onboardingValues.manuallyAddedSkills,
          }),
          ...(onboardingValues.certificates && {
            certificates: filterEmptyCertificates(onboardingValues.certificates),
          }),
          ...(onboardingValues.education && {
            education: filterEmptyEducation(onboardingValues.education),
          }),
        },
      }),
    })),
  updateExpertProfileFromProfile: (tab, newValues) => {
    set((prev) => {
      if (!prev.expertProfile) {
        return prev;
      }
      let updatedValues: Partial<ExpertProfile> = {};

      switch (tab) {
        case ProfileTabEnum.Basic: {
          const basicInfoValues = newValues as Partial<BasicInfoStepValues>;
          updatedValues = {
            ...(basicInfoValues.firstName && {
              firstName: basicInfoValues.firstName,
            }),
            ...(basicInfoValues.lastName && {
              lastName: basicInfoValues.lastName,
            }),
            ...(basicInfoValues.availability && {
              availability: basicInfoValues.availability,
            }),
            ...(basicInfoValues.location && {
              location: basicInfoValues.location,
            }),
            ...(basicInfoValues.rateDesired && {
              rateDesired: basicInfoValues.rateDesired,
            }),
            ...(basicInfoValues.workType && {
              workType: basicInfoValues.workType,
            }),
          };

          break;
        }
        case ProfileTabEnum.Languages: {
          const langValues = newValues as Partial<LanguagesStepValues>;
          updatedValues = {
            ...(langValues.firstLanguageId && {
              firstLanguageId: langValues.firstLanguageId,
            }),
            ...(langValues.isOnlyFirstLanguage !== undefined && {
              isOnlyFirstLanguage: langValues.isOnlyFirstLanguage,
            }),
            ...(langValues.isOnlyFirstLanguage
              ? []
              : langValues.otherLanguages && {
                  otherLanguages: langValues.otherLanguages,
                }),
          };

          break;
        }
        case ProfileTabEnum.Experience: {
          const experienceValues = newValues as Partial<ExperienceStepValues>;

          const skillsAlreadyInProfile = prev.expertProfile.skills ?? [];
          const extractedSkills = extractSkillsFromPrevStates(
            [
              ...((experienceValues.mainWorkHistory ?? []) as WorkHistory[]),
              ...((experienceValues.otherWorkHistory ?? []) as WorkHistory[]),
            ],
            skillsAlreadyInProfile,
          );

          updatedValues = {
            ...(experienceValues.mainWorkHistory && {
              mainWorkHistory: experienceValues.isMainRoleExperience
                ? (experienceValues.mainWorkHistory as WorkHistory[])
                : [],
            }),
            ...(experienceValues.otherWorkHistory && {
              otherWorkHistory: experienceValues.isOtherRolesExperience
                ? (experienceValues.otherWorkHistory as WorkHistory[])
                : [],
            }),
            ...(experienceValues.jobPositionId && {
              jobPositionId: experienceValues.jobPositionId,
            }),
            ...(experienceValues.isMainRoleExperience !== undefined && {
              isMainRoleExperience: experienceValues.isMainRoleExperience,
            }),
            ...(experienceValues.isOtherRolesExperience !== undefined && {
              isOtherRolesExperience: experienceValues.isOtherRolesExperience,
            }),
            skills: extractedSkills,
          };

          break;
        }

        case ProfileTabEnum.Skill: {
          const skillValues = newValues as Partial<SkillStepValues & SkillLevelStepValues>;
          const manualSkillsAlreadyInProfile: Skill[] = skillValues.jobPositionSkills
            ? skillValues.jobPositionSkills.tagIds.map((tagId) => ({
                tagId,
                weight:
                  prev.expertProfile?.manuallyAddedSkills.find((tag) => tag.tagId === tagId)
                    ?.weight || 1,
              }))
            : (prev.expertProfile.manuallyAddedSkills ?? []);

          const extractedSkills = extractSkillsFromPrevStates(
            [...(skillValues.mainWorkHistory ?? []), ...(skillValues.otherWorkHistory ?? [])],
            [
              ...(prev.expertProfile.skills ?? []),
              ...(prev.expertProfile.manuallyAddedSkills ?? []),
            ],
          );

          updatedValues = {
            ...(skillValues.mainWorkHistory && {
              mainWorkHistory: skillValues.mainWorkHistory as WorkHistory[],
            }),
            ...(skillValues.otherWorkHistory && {
              otherWorkHistory: skillValues.otherWorkHistory as WorkHistory[],
            }),
            skills: extractedSkills,
            manuallyAddedSkills: manualSkillsAlreadyInProfile,
          };

          break;
        }

        case ProfileTabEnum.SkillLevel: {
          const skillLevelValues = newValues as Partial<SkillLevelStepValues>;
          updatedValues = {
            ...(skillLevelValues.skills && {
              skills: skillLevelValues.skills,
            }),
            ...(skillLevelValues.manuallyAddedSkills && {
              manuallyAddedSkills: skillLevelValues.manuallyAddedSkills,
            }),
          };

          break;
        }

        case ProfileTabEnum.Education: {
          const educationValues = newValues as Partial<EducationStepValues>;
          updatedValues = {
            ...(educationValues.certificates && {
              certificates: filterEmptyCertificates(educationValues.certificates),
            }),
            ...(educationValues.education && {
              education: filterEmptyEducation(educationValues.education),
            }),
          };

          break;
        }

        default:
          throw new Error(`Unhandled tab update: ${tab}`);
      }

      return {
        expertProfile: {
          ...prev.expertProfile,
          ...updatedValues,
        },
      };
    });
  },
  clearExpertProfile: () => set({ expertProfile: null }),
}));
